import { useReduxLoading } from '@core/hooks';
import { mensagemCampoObrigatorio } from '@core/util/mensagemUtil';
import { permissaoUtils } from '@core/util/permissaoUtil';
import { Grid, makeStyles } from '@material-ui/core';
import { useFormik } from 'formik';
import React, { useContext, useState } from 'react';
import * as yup from 'yup';
import { ConciliacaoContext } from '../ConciliacaoContext';
import {
  BotaoConciliar,
  BotaoDesconciliar,
  ContainerExtratoForm,
} from '../containers/ContainerLancamentoExtratoBancario';
import FormularioLancamentoDespesa from '../forms/FormularioLancamentoDespesa';
import { ConciliacaoModal } from '../modal/ConciliacaoModal';
import PesquisaContaPagarModal from '../pesquisas/contaPagar/PesquisaContaPagarModal';
import VisualizacaoDespesa from '../visualizacao/VisualizacaoDespesa';

const useStyles = makeStyles((theme) => ({
  btnContainer: {
    alignSelf: 'center',
  },
}));

const ContentLancamentoDespesa = ({
  extrato,
  handlerVisualizarDespesa,
  handlerEditarDespesa,
  handlerConciliar,
  handlerDesconciliar,
}) => {
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useContext(ConciliacaoContext).modal;
  const [, setConciliacao] = useContext(ConciliacaoContext).conciliacao;
  const [formularioLancamentoDespesa] = useContext(ConciliacaoContext).formularioLancamentoDespesa;
  const [extratoSelecionado, setExtratoSelecionado] = useContext(ConciliacaoContext).extratoSelecionado;
  const [conciliacoes, setConciliacoes] = useContext(ConciliacaoContext).conciliacoes;

  useReduxLoading(loading);

  const classes = useStyles();

  const getInitialValues = (registro) => ({
    ...registro,
    idDespesa: registro?.despesa?.id,
    fornecedorDespesa: registro?.despesa?.fornecedor?.id,
    categoriaDespesa: registro?.despesa?.categoria?.id,
    valorDespesa: Math.abs(extrato?.valor),
    formaPagamentoDespesa: registro?.despesa?.formaPagamento,
    numeroChequeDespesa: registro?.despesa?.numeroCheque,
  });

  const FormDespesaSchema = yup.object().shape({
    fornecedorDespesa: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o fornecedor')),
    categoriaDespesa: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('a categoria')),
    valorDespesa: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('o valor')),
    formaPagamentoDespesa: yup
      .string()
      .nullable()
      .required(mensagemCampoObrigatorio('a forma de pagamento')),
    numeroChequeDespesa: yup
      .string()
      .nullable()
      .test('validaNumeroChequeDespesa', mensagemCampoObrigatorio('o número do cheque'), function(numeroChequeDespesa) {
        const { formaPagamentoDespesa } = this.parent;

        return formaPagamentoDespesa !== 'CHEQUE' || !!numeroChequeDespesa;
      }),
  });

  const handlerConciliarDespesa = async (values) => {
    let conciliacaoAux = conciliacoes.filter((c) => c.id === extrato.id)[0];
    conciliacaoAux.contaPagarList = [
      {
        fornecedor: values?.fornecedorDespesa,
        categorias: [values?.categoriaDespesa],
        valor: values?.valorDespesa,
        formaPagamento: values?.formaPagamentoDespesa,
        numeroCheque: values?.numeroChequeDespesa,
        formulario: true,
      },
    ];
    await handlerConciliar(conciliacaoAux);
    formik.resetForm(getInitialValues);
  };

  const formik = useFormik({
    initialValues: getInitialValues(formularioLancamentoDespesa),
    validationSchema: FormDespesaSchema,
    onSubmit: handlerConciliarDespesa,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const handlerLimparInformacoesFormulario = () => {
    if (extrato.contaPagarList && extrato.contaPagarList?.length !== 0) {
      handlerLimparInformacoesVisualizacao(extrato.contaPagarList[0], extrato.contaPagarList?.length > 1);
    } else {
      formik.resetForm(getInitialValues);
    }
  };

  const abrirModalDespesaUnica = () => {
    setConciliacao(extrato);
    setModal(ConciliacaoModal.cadastrarContaPagarModal);
  };

  const handlerPesquisarDespesa = () => {
    setLoading(true);
    setExtratoSelecionado(extrato);
    setModal(ConciliacaoModal.pesquisaContaPagar);
    setLoading(false);
  };

  const handlerLancarDespesa = () => {
    abrirModalDespesaUnica();
  };

  const handlerCriarTransferencia = () => {
    setLoading(true);

    setConciliacoes(
      conciliacoes.map((c) => {
        if (c.id === extrato.id) {
          c.criarTransferencia = true;
        }
        return c;
      })
    );

    setLoading(false);
  };

  const carregaContasPagarNoCard = () => {
    setModal('');
    conciliacoes
      .filter((c) => c.id === extratoSelecionado.id)
      .forEach((c) => {
        c.contaPagarList = extratoSelecionado.contaPagarList;
      });
    setConciliacoes(conciliacoes);
    setExtratoSelecionado({});
  };

  const handlerLimparInformacoesVisualizacao = (contaSelecionada, listagem) => {
    const lista = extrato.contaPagarList.filter((conta) => conta.id !== contaSelecionada.id);

    if (lista.length > 0 && !listagem) {
      extrato.contaPagarList = lista;
    } else {
      delete extrato.contaPagarList;
    }

    setExtratoSelecionado(extrato);
    carregaContasPagarNoCard();
  };

  const visualizacaoUnica = () => {
    return extrato?.contaPagarList && extrato?.contaPagarList.length === 1 && !extrato?.contaPagarList[0].formulario;
  };

  const visualizacaoLista = () => {
    return extrato?.contaPagarList && extrato?.contaPagarList.length > 1;
  };

  const visualizacaoTransferencia = () => {
    return extrato?.criarTransferencia && !extrato?.transferenciaBancaria;
  };

  return extrato &&
    !extrato?.transferenciaBancaria &&
    extrato?.valor != null &&
    extrato?.valor <= 0 &&
    !extrato?.contaReceberList &&
    !visualizacaoTransferencia() ? (
    <>
      <Grid item xs={2} className={classes.btnContainer}>
        {!extrato?.id ? null : extrato.conciliado !== true ? (
          <BotaoConciliar
            onClick={() => {
              extrato.contaPagarList ? handlerConciliar(extrato) : formik.submitForm();
            }}
          />
        ) : (
          <BotaoDesconciliar
            onClick={() => {
              handlerDesconciliar(extrato);
            }}
          />
        )}
      </Grid>
      <Grid item xs={5}>
        <ContainerExtratoForm
          values={extrato}
          handlerPesquisar={handlerPesquisarDespesa}
          handlerVisualizar={handlerVisualizarDespesa}
          handlerLancarConta={handlerLancarDespesa}
          handlerCriarTransferencia={handlerCriarTransferencia}
          handlerEditar={handlerEditarDespesa}
          handlerLimparInformacoes={handlerLimparInformacoesFormulario}
          temPermissaoParaVisualizar={permissaoUtils.contaPagar().visualizar}
          temPermissaoParaEditar={permissaoUtils.contaPagar().editar}
          temPermissaoParaCriar={permissaoUtils.contaPagar().cadastrar}
        >
          {visualizacaoUnica() || visualizacaoLista() ? (
            <VisualizacaoDespesa
              extrato={extrato}
              contaPagarList={extrato.contaPagarList}
              conciliado={extrato.conciliado}
              handlerVisualizar={handlerVisualizarDespesa}
              handlerEditar={handlerEditarDespesa}
              handlerLimparInformacoes={handlerLimparInformacoesVisualizacao}
              exibirTooltipSemCorrespondencia={extrato.valor === 0}
              temPermissaoParaVisualizar={permissaoUtils.contaPagar().visualizar}
              temPermissaoParaEditar={permissaoUtils.contaPagar().editar}
              temPermissaoParaCadastrar={permissaoUtils.contaPagar().cadastrar}
              handlerLancarConta={handlerLancarDespesa}
            />
          ) : (
            <FormularioLancamentoDespesa formik={formik} extrato={extrato} />
          )}
        </ContainerExtratoForm>
      </Grid>
      {modal === ConciliacaoModal.pesquisaContaPagar && extratoSelecionado?.id === extrato.id && (
        <PesquisaContaPagarModal onSubmit={carregaContasPagarNoCard} />
      )}
    </>
  ) : null;
};

export default ContentLancamentoDespesa;
